import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { StatusPill } from '../components/StatusPill';
import { Async } from '../components/Async';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../lib/auth';
import { getClients, getAccrued, getUnlinkedClinics, createClient, generateClientDraft, draftResultMessage, ApiError, type NewClient, type ClientAccrued } from '../lib/api';
import { fmtCents0, fmtNum } from '../lib/format';

export const ClientsPage = () => {
  const { can } = useAuth();
  const create = can('clients:create');
  const canRun = can('billing:run');
  const state = useFetch(getClients);
  const accruedState = useFetch(() => getAccrued());
  const accrued = new Map<string, ClientAccrued>((accruedState.data?.clients ?? []).map((a) => [a.clientId, a]));
  const unlinkedState = useFetch(getUnlinkedClinics);
  const [adding, setAdding] = useState(false);
  const [linking, setLinking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drafting, setDrafting] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function reloadAll() {
    state.reload();
    unlinkedState.reload();
    accruedState.reload();
  }

  async function generateDraft(clientId: string, name: string) {
    if (!window.confirm(`Generate a draft for ${name}? Any existing open drafts for this client are superseded (voided).`)) return;
    setError(null);
    setMsg(null);
    setDrafting(clientId);
    try {
      const r = await generateClientDraft(clientId, { supersede: true });
      setMsg(draftResultMessage(r));
      accruedState.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not generate draft');
    } finally {
      setDrafting(null);
    }
  }

  async function linkClinic(clinic: string) {
    setError(null);
    setLinking(clinic);
    try {
      await createClient({ name: clinic, type: 'telemed', pharmacyIds: [clinic] });
      await reloadAll();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create client');
    } finally {
      setLinking(null);
    }
  }

  async function onCreate(body: NewClient) {
    setError(null);
    try {
      await createClient(body);
      setAdding(false);
      await reloadAll();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create client');
    }
  }

  return (
    <Page>
      <PageHeader
        title="Clients"
        sub={state.data ? `${state.data.clients.length} clients` : undefined}
        actions={
          create ? (
            <button className="btn btn--primary" onClick={() => setAdding((a) => !a)}>
              {adding ? 'Cancel' : 'Add client'}
            </button>
          ) : undefined
        }
      />

      {error && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(239,68,68,.08)', color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>
          {error}
        </div>
      )}
      {msg && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(16,185,129,.08)', color: 'var(--status-success-ink,#047857)', fontSize: 13 }}>
          {msg} <Link to="/invoices?status=draft">View drafts →</Link>
        </div>
      )}

      {create && (unlinkedState.data?.clinics.length ?? 0) > 0 && (
        <PCard style={{ padding: 14, borderLeft: '3px solid var(--status-attention-ink,#b45309)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            {unlinkedState.data!.clinics.length} pharmacy clinic(s) dispensing with no billing client
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {unlinkedState.data!.clinics.map((c) => (
              <div key={c.clinic} style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--p-border)', borderRadius: 8, padding: '6px 10px' }}>
                <span style={{ fontWeight: 500, fontSize: 13 }}>{c.clinic}</span>
                <span className="muted" style={{ fontSize: 12 }}>{fmtNum(c.completed)} completed · {fmtNum(c.orders)} orders</span>
                <button className="btn" disabled={linking === c.clinic} onClick={() => linkClinic(c.clinic)}>
                  {linking === c.clinic ? 'Adding…' : 'Add'}
                </button>
              </div>
            ))}
          </div>
          <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
            Adds a telemed client (active) with this clinic code. Rename or adjust pricing after.
          </div>
        </PCard>
      )}

      {adding && create && <AddClientForm onCreate={onCreate} onCancel={() => setAdding(false)} />}

      <Async state={state} isEmpty={(d) => d.clients.length === 0} emptyLabel="No clients yet.">
        {({ clients }) => (
          <PCard>
            <table className="p-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Pharmacies</th>
                  <th>Patient floor</th>
                  <th>Base fee</th>
                  <th>Platform</th>
                  <th>Pharmacy</th>
                  <th>Shipping</th>
                  <th>Dr network</th>
                  <th style={{ textAlign: 'right' }}>Unbilled</th>
                  <th style={{ textAlign: 'right' }}>Outstanding</th>
                  {canRun && <th style={{ textAlign: 'right' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 500 }}><Link to={`/clients/${c.id}`}>{c.name}</Link></td>
                    <td>
                      <StatusPill tone={c.type === 'telemed' ? 'in-progress' : 'neutral'}>
                        {c.type === 'telemed' ? 'Telemed' : 'Express'}
                      </StatusPill>
                    </td>
                    <td>
                      <StatusPill tone={c.status === 'active' ? 'success' : 'neutral'}>
                        {c.status === 'active' ? 'Active' : c.status === 'paused' ? 'Paused' : 'Churned'}
                      </StatusPill>
                    </td>
                    <td className="tabular muted">{c.pharmacyCount}</td>
                    <td className="tabular muted">{c.type === 'telemed' ? '—' : fmtNum(c.minPatients)}</td>
                    <td className="tabular">{c.type === 'telemed' ? '—' : fmtCents0(c.baseFeeCents)}</td>
                    <td className="tabular muted">{fmtCents0(c.fees.platform)}</td>
                    <td className="tabular muted">{fmtCents0(c.fees.pharmacy)}</td>
                    <td className="tabular muted">{fmtCents0(c.fees.shipping)}</td>
                    <td className="tabular muted">{fmtCents0(c.fees.drNetwork)}</td>
                    <td className="tabular" style={{ textAlign: 'right' }}>
                      {(() => {
                        const a = accrued.get(c.id);
                        if (!accruedState.data) return <span className="muted">…</span>;
                        if (!a || a.totalCents <= 0) return <span className="muted">—</span>;
                        const title = `Pharmacy ${fmtCents0(a.pharmacyCents)} (${a.pharmacyOrders} orders) · Shipping ${fmtCents0(a.shippingCents)} (${a.shippingShipments} shipments)`;
                        return (
                          <span style={{ fontWeight: 600 }} title={title}>
                            {fmtCents0(a.totalCents)}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="tabular" style={{ textAlign: 'right', fontWeight: c.outstandingCents > 0 ? 600 : 400 }}>
                      {c.outstandingCents > 0 ? (
                        <span style={{ color: 'var(--status-error-ink,#b91c1c)' }} title={`${c.openInvoiceCount} open invoice(s)`}>
                          {fmtCents0(c.outstandingCents)}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    {canRun && (
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button className="btn" disabled={drafting === c.id} onClick={() => generateDraft(c.id, c.name)}>
                          {drafting === c.id ? 'Generating…' : 'Generate draft'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </PCard>
        )}
      </Async>
    </Page>
  );
};

function AddClientForm({ onCreate, onCancel }: { onCreate: (b: NewClient) => Promise<void>; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [type, setType] = useState<'express' | 'telemed'>('telemed');
  const [pharmacyIds, setPharmacyIds] = useState('');
  const [coreAccountId, setCoreAccountId] = useState('');
  const [minPatients, setMinPatients] = useState('');
  const [baseFee, setBaseFee] = useState('');
  const [perPatient, setPerPatient] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body: NewClient = {
        name,
        type,
        pharmacyIds: pharmacyIds.split(',').map((s) => s.trim()).filter(Boolean),
      };
      if (type === 'express') {
        body.coreAccountId = coreAccountId || undefined;
        body.platformPlan = {
          minPatients: parseInt(minPatients) || 0,
          baseFeeCents: Math.round((parseFloat(baseFee) || 0) * 100),
          perPatientCents: Math.round((parseFloat(perPatient) || 0) * 100),
        };
      }
      await onCreate(body);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PCard style={{ padding: 16 }}>
      <form onSubmit={submit} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        <Field label="Name"><input className="select" value={name} onChange={(e) => setName(e.target.value)} required /></Field>
        <Field label="Type">
          <select className="select" value={type} onChange={(e) => setType(e.target.value as 'express' | 'telemed')}>
            <option value="telemed">Telemed</option>
            <option value="express">Express</option>
          </select>
        </Field>
        <Field label="Pharmacy code(s)">
          <input className="select" value={pharmacyIds} onChange={(e) => setPharmacyIds(e.target.value)} placeholder="e.g. REMEDORA" />
        </Field>
        {type === 'express' && (
          <>
            <Field label="Core DB"><input className="select" value={coreAccountId} onChange={(e) => setCoreAccountId(e.target.value)} placeholder="e.g. MDEXAM" /></Field>
            <Field label="Patient floor"><input className="select" type="number" min="0" value={minPatients} onChange={(e) => setMinPatients(e.target.value)} required /></Field>
            <Field label="Base fee ($)"><input className="select" type="number" step="0.01" min="0" value={baseFee} onChange={(e) => setBaseFee(e.target.value)} required /></Field>
            <Field label="Per patient ($)"><input className="select" type="number" step="0.01" min="0" value={perPatient} onChange={(e) => setPerPatient(e.target.value)} required /></Field>
          </>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? 'Creating…' : 'Create client'}</button>
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </PCard>
  );
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--p-muted-fg)' }}>
    {label}
    {children}
  </label>
);
